import React, { useState, useRef, useEffect, memo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../AuthContext';
import { useQuery } from '../QueryContext';
import './Header.css';
import loginIcon from '../assets/dimassi_logo.svg';
import thomasIconImg from '../assets/icono-t500.png';

// Componentes SVG de Iconos existentes
const HomeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
    <path d="M0 0h24v24H0z" fill="none"/><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
  </svg>
);

const SunIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"></circle><line x1="12" y1="1" x2="12" y2="3"></line><line x1="12" y1="21" x2="12" y2="23"></line><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line><line x1="1" y1="12" x2="3" y2="12"></line><line x1="21" y1="12" x2="23" y2="12"></line><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
  </svg>

);

const MoonIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
  </svg>
);

const BookmarkArrowIcon = ({ isOpen }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {isOpen ? <polyline points="15 18 9 12 15 6" /> : <polyline points="9 18 15 12 9 6" />}
  </svg>
);

const LogoutIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line>
  </svg>
);

const ThomasBotIcon = () => (
  <img 
    src={thomasIconImg} 
    alt="Thomas T500" 
    style={{ width: '100px', height: '100px', objectFit: 'contain' }} 
    className="thomas-fab-icon-img"
  />
);

const SendIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const AccordionSection = ({ title, links = [], onCloseSidebar = () => {} }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="accordion-section">
      <button className={`accordion-toggle ${open ? 'open' : ''}`} onClick={() => setOpen(o => !o)} aria-expanded={open}>
        {title}<span className="accordion-icon" style={{ marginLeft: 8 }}>{open ? '▾' : '▸'}</span>
      </button>
      <div className={`accordion-divider ${open ? 'show' : ''}`} aria-hidden />
      <AnimatePresence>
        {open && (
          <motion.div
            className="accordion-panel"
            initial={{ height: 0, opacity: 0, marginTop: 0 }}
            animate={{ height: 'auto', opacity: 1, marginTop: 6 }}
            exit={{ height: 0, opacity: 0, marginTop: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            style={{ overflow: 'hidden' }}
          >
            {links.map((l, index) => (
              <motion.div key={l.to} initial={{ x: -10, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -10, opacity: 0 }} transition={{ delay: index * 0.05, duration: 0.2 }}>
                <NavLink to={l.to} className={({ isActive }) => "sidebar-link submenu-link" + (isActive ? " active" : "")} onClick={() => { onCloseSidebar(); }}>
                  {l.label}
                </NavLink>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

function HeaderComponent() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');
  const sidebarRef = useRef(null);
  const { logout, profile, hasAccess } = useAuth();
  const { askT500 } = useQuery();
  const navigate = useNavigate();

  const [chatOpen, setChatOpen] = useState(false);
  const [inputMessage, setInputMessage] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const createWelcomeMessage = () => ({
    id: Date.now(),
    sender: 'thomas',
    text: `Hola ${profile?.nombre || 'usuario'}, soy Thomas (T500 Integrated). ¿En qué reporte o consulta analítica te asisto hoy?`
  });
  const [messages, setMessages] = useState([createWelcomeMessage()]);
  const chatEndRef = useRef(null);

  useEffect(() => {
    document.body.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  const handleSignOut = async () => {
    await logout();
  }

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isThinking]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target) && !event.target.closest('.sidebar-bookmark-tab')) {
        setSidebarOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidebarRef]);

  const handleClearChat = () => {
    setMessages([createWelcomeMessage()]);
    setInputMessage("");
    setIsThinking(false);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isThinking) return;

    const userMsg = { id: Date.now(), sender: 'user', text: inputMessage.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInputMessage("");
    setIsThinking(true);

    try {
      const data = await askT500(userMsg.text, {
        id: profile?.id,
        username: profile?.username,
        nombre: profile?.nombre,
        cargo: profile?.cargo
      });
      
      const thomasMsg = {
        id: Date.now() + 1,
        sender: 'thomas',
        text: data.response,
        link: data.link || null
      };
      setMessages(prev => [...prev, thomasMsg]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        id: Date.now(), 
        sender: 'thomas', 
        text: 'Error en la matriz de enlace con T500. Verifica tu conexión.' 
      }]);
    } finally {
      setIsThinking(false);
    }
  };

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Definir TODOS los links posibles
  const allSalesLinks = [
    { to: '/daily', label: 'Daily' },
    { to: '/catalogo', label: 'Catalogo' },
  ];

  const allAdminLinks = [
    { to: '/facturacion', label: 'Facturación' },
    { to: '/reportesav', label: 'Reportes AV' },
    { to: '/pedidos', label: 'Pedidos' },
    { to: '/pedidosmasivos', label: 'Pedidos Masivos' },
  ];

  const allCobLinks = [
    { to: '/cobranzas', label: 'Cobranzas' },
    { to: '/cxccob', label: 'Cuentas x Cobrar' },
  ];

  const allAlmLinks = [
    { to: '/panelalmacen', label: 'Panel' },
    { to: '/almacen', label: 'Almacén' },
  ];

  const allTradeLinks = [
    { to: '/tradeform', label: 'Trade Form (Solicitudes)' },
    { to: '/tradetrack', label: 'Trade Track (Seguimientos)' },
  ];

  const allHTalentLinks = [
    { to: '/talentohumano', label: 'Talento Humano' },

  ];

  // Filtrar links según permisos del usuario
  const filteredSalesLinks = allSalesLinks.filter(l => hasAccess(l.to));
  const filteredAdminLinks = allAdminLinks.filter(l => hasAccess(l.to));
  const filteredCobLinks = allCobLinks.filter(l => hasAccess(l.to));
  const filteredAlmLinks = allAlmLinks.filter(l => hasAccess(l.to));
  const filteredTradeLinks = allTradeLinks.filter(l => hasAccess(l.to));
  const filteredHTalentLinks = allHTalentLinks.filter(l => hasAccess(l.to));

  // Para móvil, solo mostrar Daily si tiene acceso
    // Para móvil, mostrar Daily y Catalogo si tiene acceso
  const mobileSalesLinks = isMobile 
    ? filteredSalesLinks.filter(l => l.to === '/daily' || l.to === '/catalogo')
    : filteredSalesLinks;


  // Elegir qué links mostrar según dispositivo
  const salesLinksToShow = isMobile ? mobileSalesLinks : filteredSalesLinks;

  return (
    <>
      <nav className="navbar">
        <div className="nav-section left">
          <img src={loginIcon} alt="Dimassi Sales" className="header-logo" />
        </div>
        <div className="nav-section right">
          <NavLink to="/home" className={({ isActive }) => "home-link" + (isActive ? " active" : "")}> <HomeIcon/></NavLink>
        </div>
      </nav>

      {/* Pestaña tipo bookmark */}
      <div className={`sidebar-bookmark-tab ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(!sidebarOpen)}>
        <BookmarkArrowIcon isOpen={sidebarOpen} />
      </div>

      {/* Sidebar y Overlay */}
      <div className={`sidebar-overlay ${sidebarOpen ? 'show' : ''}`} onClick={() => setSidebarOpen(false)}></div>
      <div ref={sidebarRef} className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="profile-hero">
            <div className="profile-avatar-container">
              <div className="profile-avatar-placeholder" title={profile?.nombre || 'Usuario'}>
                {profile?.nombre ? profile.nombre.split(' ').map(n => n[0]).slice(0,2).join('') : 'U'}
              </div>
            </div>
            <div className="profile-info">
              <h4 className="profile-name">{profile?.nombre || 'Usuario'}</h4>
              <p className="profile-role">{profile?.cargo || 'Sin cargo'}</p>
            </div>
          </div>
        </div>
        <div className="sidebar-main-content">
          {/* Home siempre visible */}
          <NavLink to="/home" className={({ isActive }) => "sidebar-link submenu-link" + (isActive ? " active" : "")} onClick={() => setSidebarOpen(false)}>
            <HomeIcon /> <span style={{ marginLeft: 10 }}>Home</span>
          </NavLink>

          {/* Gestión de Ventas */}
          {salesLinksToShow.length > 0 && (
            <AccordionSection title="Gestión de Ventas" links={salesLinksToShow} onCloseSidebar={() => setSidebarOpen(false)} />
          )}

          {/* Admin. Ventas */}
          {filteredAdminLinks.length > 0 && (
            <AccordionSection title="Admin. Ventas" links={filteredAdminLinks} onCloseSidebar={() => setSidebarOpen(false)} />
          )}

          {/* Cobranzas */}
          {filteredCobLinks.length > 0 && (
            <AccordionSection title="Cobranzas" links={filteredCobLinks} onCloseSidebar={() => setSidebarOpen(false)} />
          )}

          {/* Almacén */}
          {filteredAlmLinks.length > 0 && (
            <AccordionSection title="Almacén" links={filteredAlmLinks} onCloseSidebar={() => setSidebarOpen(false)} />
          )}

          {/* Trade Marketing */}
          {filteredTradeLinks.length > 0 && (
            <AccordionSection title="Trade Marketing" links={filteredTradeLinks} onCloseSidebar={() => setSidebarOpen(false)} />
          )}

          {/* Trade Marketing */}
          {filteredHTalentLinks.length > 0 && (
            <AccordionSection title="Talento Humano" links={filteredHTalentLinks} onCloseSidebar={() => setSidebarOpen(false)} />
          )}


          {/* BOTÓN DE ADMINISTRACIÓN EXCLUSIVO PARA COORDINADOR */}
          {profile?.cargo === "Coordinador de Inteligencia de Negocio" && (
            <div style={{ marginTop: 8 }}>
              <NavLink 
                to="/admin/usuarios" 
                className={({ isActive }) => "sidebar-link submenu-link" + (isActive ? " active" : "")} 
                onClick={() => setSidebarOpen(false)}
                style={{ color: '#3b82f6', marginBottom: '4px' }}
              >
                <span style={{ marginRight: 10 }}>👥</span> Administrar Usuarios
              </NavLink>
              <NavLink 
                to="/admin/permisos" 
                className={({ isActive }) => "sidebar-link submenu-link" + (isActive ? " active" : "")} 
                onClick={() => setSidebarOpen(false)}
                style={{ color: '#ffb74d' }}
              >
                <span style={{ marginRight: 10 }}>🔐</span> Administrar Permisos
              </NavLink>
            </div>
          )}

          <div style={{ marginTop: 8 }}>
            <button className="sidebar-link submenu-link" onClick={() => { setSidebarOpen(false); handleSignOut(); }}>
              <LogoutIcon /><span style={{ marginLeft: 10 }}>Cerrar Sesión</span>
            </button>
          </div>
        </div>
        <div className="sidebar-footer">
          <div className="theme-switch-wrapper">
            <SunIcon />
            <label className="theme-switch">
              <input type="checkbox" checked={theme === 'dark'} onChange={toggleTheme} />
              <span className="slider round"></span>
            </label>
            <MoonIcon />
          </div>
        </div>
      </div>

      {/* THOMAS (T500) - SOLO PARA COORDINADOR */}
      {profile?.cargo === "Coordinador de Inteligencia de Negocio" && (
        <>
          <motion.button 
            className={`thomas-fab-button ${chatOpen ? 'active' : ''}`}
            onClick={() => setChatOpen(!chatOpen)}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            title="Consultar a Thomas T500"
          >
            {chatOpen ? <CloseIcon /> : <ThomasBotIcon />}
          </motion.button>

          <AnimatePresence>
            {chatOpen && (
              <motion.div 
                className="thomas-chat-window"
                initial={{ opacity: 0, y: 50, scale: 0.92 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 40, scale: 0.95 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
              >
                <div className="thomas-chat-header">
                  <img 
                      src={thomasIconImg} 
                      alt="Thomas T500" 
                      className="thomas-header-avatar" 
                      style={{ objectFit: 'cover', borderRadius: '50%' }} 
                    />
                  <div className="thomas-header-info">
                    <h5>Thomas</h5>
                    <span>T500 Integrated • Core Activo</span>
                  </div>
                  <button
                    type="button"
                    className="thomas-clear-chat-button"
                    onClick={handleClearChat}
                    title="Limpiar chat"
                  >
                    Limpiar
                  </button>
                </div>

                <div className="thomas-chat-messages">
                  {messages.map((msg) => (
                    <div key={msg.id} className={`thomas-msg-bubble ${msg.sender}`}>
                      <div className="thomas-msg-text">{msg.text}</div>
                      {msg.sender === 'thomas' && msg.link && (
                        <button
                          type="button"
                          className="thomas-msg-link-button"
                          onClick={() => {
                            navigate(msg.link.path);
                            setChatOpen(false);
                          }}
                        >
                          {msg.link.label}
                        </button>
                      )}
                    </div>
                  ))}
                  
                  {isThinking && (
                    <div className="thomas-msg-bubble thomas thinking">
                      <div className="thomas-typing-indicator">
                        <span></span><span></span><span></span>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>

                <form className="thomas-chat-input-area" onSubmit={handleSendMessage}>
                  <input 
                    type="text" 
                    placeholder="Pregúntale a Thomas sobre ventas, bultos..." 
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    disabled={isThinking}
                  />
                  <button type="submit" disabled={!inputMessage.trim() || isThinking}>
                    <SendIcon />
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </>
  );
}

const Header = memo(HeaderComponent);
export default Header;